/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

export const TRACE_VERSION = 1 as const;

export const TRACE_EVENT_TYPES = [
  "app.visibility_changed",
  "app.network_state_changed",
  "telemetry.buffer_overflow",
  "attempt.started",
  "attempt.identity_resolved",
  "attempt.room_resolved",
  "attempt.connected",
  "attempt.ended",
  "attempt.failed",
  "attempt.abandoned",
  "ui.join_requested",
  "ui.reconnect_requested",
  "ui.error_screen_shown",
  "auth.flow_started",
  "auth.login_started",
  "auth.login_succeeded",
  "auth.login_failed",
  "auth.guest_registration_started",
  "auth.guest_registration_succeeded",
  "auth.guest_registration_failed",
  "auth.interactive_stage_changed",
  "matrix.sync_state_changed",
  "matrix.room_membership_changed",
  "matrix.room_lookup_started",
  "matrix.room_lookup_succeeded",
  "matrix.room_join_started",
  "matrix.room_join_succeeded",
  "matrix.room_join_failed",
  "matrix.room_members_recovery_skipped",
  "matrix.room_members_recovery_started",
  "matrix.room_members_recovery_succeeded",
  "matrix.room_members_recovery_failed",
  "matrix.rtc_members_recovery_skipped",
  "matrix.rtc_members_recovery_started",
  "matrix.rtc_members_recovery_succeeded",
  "matrix.rtc_members_recovery_failed",
  "matrix.knock_started",
  "matrix.invite_received",
  "matrix.knock_rejected",
  "matrix.room_ready",
  "media.devices_snapshot",
  "media.device_selected",
  "media.permission_result",
  "media.preview_tracks_started",
  "media.preview_tracks_succeeded",
  "media.preview_tracks_failed",
  "media.audio_precreate_started",
  "media.audio_precreate_succeeded",
  "media.audio_precreate_failed",
  "media.audio_device_fallback",
  "media.track_publish_started",
  "media.track_publish_succeeded",
  "media.track_publish_failed",
  "media.device_error",
  "media.local_track_unpublished",
  "matrix_rtc.join_started",
  "matrix_rtc.join_state_changed",
  "matrix_rtc.membership_status_changed",
  "matrix_rtc.own_membership_changed",
  "matrix_rtc.membership_error",
  "matrix_rtc.probably_left_changed",
  "matrix_rtc.leave_started",
  "matrix_rtc.leave_succeeded",
  "matrix_rtc.leave_failed",
  "sfu.openid_started",
  "sfu.openid_succeeded",
  "sfu.openid_failed",
  "sfu.config_fetch_started",
  "sfu.config_fetch_succeeded",
  "sfu.config_fetch_failed",
  "sfu.focus_changed",
  "livekit.connection_segment_started",
  "livekit.call_id_resolved",
  "livekit.connection_segment_ended",
  "livekit.connect_started",
  "livekit.retry_scheduled",
  "livekit.connect_succeeded",
  "livekit.connect_failed",
  "livekit.connection_state_changed",
  "livekit.transport_fallback",
  "livekit.reconnect_started",
  "livekit.reconnect_succeeded",
  "livekit.reconnect_failed",
  "livekit.disconnected",
  "call.recovery_cycle_started",
  "call.recovery_cycle_finished",
  "call.join_succeeded",
  "call.auto_leave_triggered",
  "call.fatal_error",
  "recording.local_start_requested",
  "recording.local_started",
  "recording.local_start_failed",
  "recording.local_stop_requested",
  "recording.local_stopped",
  "recording.local_stop_failed",
  "recording.remote_state_observed",
  "protocolist.start_requested",
  "protocolist.request_succeeded",
  "protocolist.request_failed",
  "protocolist.state_changed",
  "protocolist.failed",
  "protocolist.stop_requested",
  "protocolist.stop_succeeded",
  "protocolist.stop_failed",
] as const;

export type TraceEventType = (typeof TRACE_EVENT_TYPES)[number];

export type TraceStage =
  | "app"
  | "telemetry"
  | "attempt"
  | "ui"
  | "auth"
  | "matrix"
  | "media"
  | "matrix_rtc"
  | "sfu"
  | "livekit"
  | "recovery"
  | "call"
  | "recording"
  | "protocolist";

export type TraceSeverity = "debug" | "info" | "warn" | "error" | "fatal";
export type TraceOutcome =
  | "started"
  | "succeeded"
  | "failed"
  | "cancelled"
  | "recovered"
  | "state_changed"
  | "none";
export type TraceDeliveryState = "pending" | "leased" | "acknowledged" | "dead";

export interface TraceRuntimeSnapshot {
  online: boolean;
  visibilityState: DocumentVisibilityState;
  networkType: string | null;
  effectiveConnectionType: string | null;
  rtt: number | null;
  downlink: number | null;
}

export type EmptyTracePayload = Record<string, never>;

export interface TraceErrorPayload {
  errorType?: string;
  name?: string;
  status?: number | null;
  httpStatus?: number | null;
  code?: string | number | null;
  errcode?: string | null;
  category?: string | null;
  reason?: number | null;
  reasonName?: string | null;
}

export type MatrixRoomPipelineOperation =
  | "wait_for_sync"
  | "resolve_room"
  | "alias_lookup"
  | "alias_join"
  | "invite_join"
  | "room_summary"
  | "public_join"
  | "knock"
  | "wait_for_invite"
  | "wait_for_room_ready"
  | "resolve_matrix_rtc_session";

export type RoomMembersRecoveryMode = "widget" | "standalone";
export type RoomMembersRecoveryReason =
  | "rtc_member_missing"
  | "room_member_missing"
  | "manual_reconcile";
export type RoomMembersRecoverySource =
  | "member_displaynames"
  | "media_items"
  | "manual_reconcile";
export type RoomMembersRecoverySkipReason =
  | "cooldown"
  | "already_present"
  | "foreign_room"
  | "active_hydration";
export type RtcMembersRecoveryReason =
  | "rtc_member_missing"
  | "participant_unmatched"
  | "manual_reconcile";
export type RtcMembersRecoveryMode = "widget" | "standalone" | "coordinator";
export type RtcMembersRecoverySkipReason =
  | "cooldown"
  | "already_present"
  | "foreign_room"
  | "active_hydration"
  | "invalid_participant_identity"
  | "missing_user_id"
  | "missing_device_id"
  | "room_member_still_missing";

export interface LivekitIncidentPayload {
  intentional?: boolean;
  roomState: string;
  localParticipantSid: string | null;
  localIdentity: string | null;
  localConnectionQuality: string | number;
  localTrackCount: number;
  microphoneEnabled: boolean;
  cameraEnabled: boolean;
  screenShareEnabled: boolean;
  remoteParticipantCount: number;
  activeSpeakerCount: number;
  matrixMembershipCount: number;
  matrixParticipantCount: number;
  ownMatrixMembershipPresent: boolean;
  membershipManagerStatus: string | null;
  matrixSyncState: string | null;
  matrixSyncError: TraceErrorPayload | null;
  activeFocusType: string | null;
  e2eeEnabled: boolean;
  livekitServerVersion: string | null;
  livekitServerRegion: string | null;
  runtime: TraceRuntimeSnapshot;
}

export interface MatrixRtcOwnMembershipPayload {
  matrixEventId: string | null;
  callId: string;
  callIntent: string | null;
  createdAt: number;
  expiresAt: number;
  expiresInMs: number;
  expired: boolean;
  focusSelection: string | null;
  preferredFocusTypes: string[];
}

export interface RecoveryCycleSummaryPayload {
  [key: string]: unknown;
  callSessionId: string;
  cycleId: string;
  connectionEpoch: number;
  mode: "initial_connect" | "automatic_rejoin";
  finalOutcome: "connected" | "terminal" | "cancelled";
  durationMs: number;
  presentationFreezeDurationMs: number;
  maxAttempt: number;
  signalCount: number;
  signalCounts: Record<string, number>;
  livekitDisconnectReasons: string[];
  stages: string[];
  holdingDurationsMs: Record<string, number>;
  longestHoldingStage: string | null;
  totalBackoffMs: number;
  maxBackoffMs: number;
  lastNormalizedCode: string | null;
  lastStableSignature: string | null;
  terminalCode: string | null;
  cancellationReason: string | null;
}

/** Каноническая карта payload: событие без payload-типа не компилируется. */
export interface TraceEventPayloadMap {
  "app.visibility_changed": { visibilityState: DocumentVisibilityState };
  "app.network_state_changed": TraceRuntimeSnapshot;
  "telemetry.buffer_overflow": { events: number; bytes: number };
  "attempt.started": {
    kind: TraceAttempt["kind"];
    trigger: TraceAttempt["trigger"];
  };
  "attempt.identity_resolved": EmptyTracePayload;
  "attempt.room_resolved": EmptyTracePayload;
  "attempt.connected": EmptyTracePayload;
  "attempt.ended": AttemptTerminalPayload;
  "attempt.failed": AttemptTerminalPayload;
  "attempt.abandoned": AttemptTerminalPayload;
  "ui.join_requested": {
    source: "lobby" | "widget_preload" | "skip_lobby";
  };
  "ui.reconnect_requested": EmptyTracePayload;
  "ui.error_screen_shown":
    | {
        operation: "group_call_error_boundary";
        category: string;
        recoverable: boolean;
      }
    | (TraceErrorPayload & { operation: "room_load_failure" });
  "auth.flow_started": { mode: "embedded" | "standalone" };
  "auth.login_started": { homeserverOrigin: string };
  "auth.login_succeeded": EmptyTracePayload;
  "auth.login_failed": TraceErrorPayload & {
    operation: "interactive_login" | "client_initialization";
  };
  "auth.guest_registration_started": EmptyTracePayload;
  "auth.guest_registration_succeeded": EmptyTracePayload;
  "auth.guest_registration_failed": TraceErrorPayload & {
    operation:
      | "interactive_registration"
      | "passwordless_recaptcha"
      | "passwordless_registration"
      | "passwordless_client_setup";
  };
  "auth.interactive_stage_changed": { nextStage: string | null };
  "matrix.sync_state_changed": {
    previous: string | null;
    state: string;
    catchingUp: boolean | null;
    fromCache: boolean | null;
    error: TraceErrorPayload | null;
  };
  "matrix.room_membership_changed": {
    previous: string | null;
    current: string;
    actorUserId: string | null;
    matrixEventId: string | null;
    hasReason: boolean;
  };
  "matrix.room_lookup_started": EmptyTracePayload;
  "matrix.room_lookup_succeeded": EmptyTracePayload;
  "matrix.room_join_started": {
    source: "alias" | "invite" | "public_or_summary_fallback";
  };
  "matrix.room_join_succeeded": EmptyTracePayload;
  "matrix.room_join_failed": TraceErrorPayload & {
    operation: MatrixRoomPipelineOperation;
  };
  "matrix.room_members_recovery_skipped": RoomMembersRecoveryPayload & {
    skipReason: RoomMembersRecoverySkipReason;
  };
  "matrix.room_members_recovery_started": RoomMembersRecoveryPayload;
  "matrix.room_members_recovery_succeeded": RoomMembersRecoveryPayload & {
    resolved: boolean;
  };
  "matrix.room_members_recovery_failed": RoomMembersRecoveryPayload & {
    error: TraceErrorPayload;
  };
  "matrix.rtc_members_recovery_skipped": RtcMembersRecoveryPayload & {
    skipReason: RtcMembersRecoverySkipReason;
  };
  "matrix.rtc_members_recovery_started": RtcMembersRecoveryPayload;
  "matrix.rtc_members_recovery_succeeded": RtcMembersRecoveryPayload & {
    resolved: boolean;
  };
  "matrix.rtc_members_recovery_failed": RtcMembersRecoveryPayload & {
    error: TraceErrorPayload;
  };
  "matrix.knock_started": EmptyTracePayload;
  "matrix.invite_received": EmptyTracePayload;
  "matrix.knock_rejected": EmptyTracePayload;
  "matrix.room_ready": EmptyTracePayload;
  "media.devices_snapshot": {
    kind: MediaDeviceKind;
    count: number;
    hasDefault: boolean;
  };
  "media.device_selected": {
    kind: MediaDeviceKind;
    selection: "none" | "default" | "specific";
    deviceRef: string | null;
  };
  "media.permission_result":
    | { kind: "microphone" | "camera" }
    | { audio: boolean; video: boolean };
  "media.preview_tracks_started": EmptyTracePayload;
  "media.preview_tracks_succeeded": { trackKinds: string[] };
  "media.preview_tracks_failed": TraceErrorPayload;
  "media.audio_precreate_started": {
    selection: "browser" | "default" | "specific";
  };
  "media.audio_precreate_succeeded": { trackCreated: boolean };
  "media.audio_precreate_failed": TraceErrorPayload & {
    selection: "browser" | "default" | "specific";
  };
  "media.audio_device_fallback": { fallback: "default" };
  "media.track_publish_started": MediaTrackPayload;
  "media.track_publish_succeeded": MediaTrackPayload;
  "media.track_publish_failed": TraceErrorPayload & MediaTrackPayload;
  "media.device_error": TraceErrorPayload;
  "media.local_track_unpublished": MediaTrackPayload;
  "matrix_rtc.join_started": EmptyTracePayload;
  "matrix_rtc.join_state_changed":
    | { isJoined: boolean }
    | (TraceErrorPayload & { operation: "join_room_session" });
  "matrix_rtc.membership_status_changed": {
    previous: string | null;
    current: string;
  };
  "matrix_rtc.own_membership_changed": {
    previousOwn: MatrixRtcOwnMembershipPayload | null;
    currentOwn: MatrixRtcOwnMembershipPayload | null;
    membershipManagerStatus: string | null;
  };
  "matrix_rtc.membership_error": TraceErrorPayload & {
    operation: "membership_manager";
    isJoined: boolean;
    ownMembershipPresent: boolean;
    membershipManagerStatus: string | null;
  };
  "matrix_rtc.probably_left_changed": { probablyLeft: boolean };
  "matrix_rtc.leave_started": { cause: "user" | "error" };
  "matrix_rtc.leave_succeeded": EmptyTracePayload;
  "matrix_rtc.leave_failed": TraceErrorPayload;
  "sfu.openid_started": EmptyTracePayload;
  "sfu.openid_succeeded": EmptyTracePayload;
  "sfu.openid_failed": TraceErrorPayload;
  "sfu.config_fetch_started": { focusOrigin: string | null };
  "sfu.config_fetch_succeeded": { livekitOrigin: string | null };
  "sfu.config_fetch_failed": TraceErrorPayload;
  "sfu.focus_changed":
    | { source: "warm_up"; focusOrigin: string | null }
    | { previousOrigin: string | null; currentOrigin: string | null };
  "livekit.connection_segment_started": {
    trigger: TraceConnection["trigger"];
    ordinal: number;
  };
  "livekit.call_id_resolved": { callId: string };
  "livekit.connection_segment_ended": {
    state: Exclude<TraceConnectionState, "connecting" | "connected">;
    reasonCode: string | null;
  };
  "livekit.connect_started": {
    attempt: number;
    maxAttempts: number;
    livekitOrigin: string | null;
  };
  "livekit.retry_scheduled": { attempt: number; retryDelayMs: number };
  "livekit.connect_succeeded": { attempt: number };
  "livekit.connect_failed": TraceErrorPayload & { attempt: number };
  "livekit.connection_state_changed": {
    previous: string | null;
    state: string;
  };
  // Переход на путь поверх TLS, когда обычный не встал (CTO 18.09.2026).
  "livekit.transport_fallback": TraceErrorPayload & {
    // Не `reason`: в TraceErrorPayload это поле уже занято числом.
    fallbackReason: "timeout" | "connect_failed";
    timeoutMs: number;
  };
  "livekit.reconnect_started": LivekitIncidentPayload;
  "livekit.reconnect_succeeded": LivekitIncidentPayload;
  "livekit.reconnect_failed": LivekitIncidentPayload;
  "livekit.disconnected": LivekitIncidentPayload;
  "call.recovery_cycle_started": {
    callSessionId: string;
    cycleId: string;
    connectionEpoch: number;
    mode: "initial_connect" | "automatic_rejoin";
  };
  "call.recovery_cycle_finished": RecoveryCycleSummaryPayload;
  "call.join_succeeded": { hasCallId: boolean };
  "call.auto_leave_triggered": EmptyTracePayload;
  "call.fatal_error":
    | (TraceErrorPayload & {
        operation: "unsupported_runtime" | "app_initialization";
      })
    | (TraceErrorPayload & {
        operation: "group_call_error_boundary";
        screen: "InCallView" | "GroupCallView";
        isJoined: boolean;
        left: boolean;
        preload: boolean;
        skipLobby: boolean;
        participantCount: number;
        membershipStatus: string | null;
      });
  "recording.local_start_requested": EmptyTracePayload;
  "recording.local_started": { mimeType: string };
  "recording.local_start_failed": TraceErrorPayload & {
    operation:
      | "recorder_setup"
      | "media_recorder_event"
      | "recording_state_event";
    recorderState: "inactive" | "recording" | "paused" | "unavailable";
  };
  "recording.local_stop_requested": EmptyTracePayload;
  "recording.local_stopped": { blobBytes: number };
  "recording.local_stop_failed": TraceErrorPayload;
  "recording.remote_state_observed": {
    action: string;
    type: string;
    isLocalUser: boolean;
  };
  "protocolist.start_requested": EmptyTracePayload;
  "protocolist.request_succeeded": EmptyTracePayload;
  "protocolist.request_failed": TraceErrorPayload;
  "protocolist.state_changed": {
    previous: string;
    current: string;
    signal: string;
  };
  "protocolist.failed": {
    operation: "matrix_status_transition";
    previous: string;
    current: string;
    signal: "matrix_status";
  };
  "protocolist.stop_requested": EmptyTracePayload;
  "protocolist.stop_succeeded": EmptyTracePayload;
  "protocolist.stop_failed": TraceErrorPayload;
}

interface AttemptTerminalPayload {
  finalOutcome: NonNullable<TraceAttempt["finalOutcome"]>;
  terminalStage: TraceStage;
}

interface RoomMembersRecoveryPayload {
  mode: RoomMembersRecoveryMode;
  reason: RoomMembersRecoveryReason;
  forceServer: boolean;
  source: RoomMembersRecoverySource;
  userId: string | null;
}

interface RtcMembersRecoveryPayload {
  mode: RtcMembersRecoveryMode;
  reason: RtcMembersRecoveryReason;
  forceServer: boolean;
  source: RoomMembersRecoverySource;
  userId: string | null;
  deviceId: string | null;
  participantIdentity: string | null;
}

interface MediaTrackPayload {
  kind: string;
  source: string;
}

export interface TruncatedTracePayload {
  truncated: true;
  originalKeys: string[];
}

export type TraceWirePayload =
  | TraceEventPayloadMap[TraceEventType]
  | TruncatedTracePayload;

export interface TraceSession extends TraceRuntimeSnapshot {
  sessionId: string;
  traceVersion: typeof TRACE_VERSION;
  projectionRevision: number;
  startedAt: string;
  endedAt: string | null;
  lastSequence: number;
  endReason: "normal" | "page_unload" | "superseded" | "unknown" | null;
  appMode: "standalone" | "embedded";
  platformFamily: "web" | "android" | "ios" | "desktop";
  hostPlatform: "windows" | "macos" | "linux" | "android" | "ios" | "unknown";
  browserFamily:
    | "chrome"
    | "safari"
    | "firefox"
    | "edge"
    | "webview"
    | "unknown";
  browserContext:
    | "desktop-browser"
    | "mobile-browser"
    | "desktop-app-webview"
    | "mobile-app-webview"
    | "unknown";
  homeserver: string | null;
  backendBaseUrl: string | null;
  livekitBaseUrl: string | null;
  buildVersion: string;
  gitSha: string | null;
}

/** Локальное представление session в IndexedDB. */
export interface StoredTraceSession extends TraceSession {
  createdAt: string;
  lastEventAt: string;
  approximateBytes: number;
}

export type TraceAttemptState =
  | "preparing"
  | "joining"
  | "connected"
  | "reconnecting"
  | "ended"
  | "failed"
  | "abandoned";

export interface TraceAttempt {
  attemptId: string;
  sessionId: string;
  projectionRevision: number;
  parentAttemptId: string | null;
  ordinal: number;
  kind: "initial_join" | "manual_rejoin";
  trigger:
    | "call_route"
    | "lobby_join"
    | "widget_join"
    | "skip_lobby"
    | "error_recovery";
  startedAt: string;
  endedAt: string | null;
  state: TraceAttemptState;
  finalOutcome:
    | "user_left"
    | "recovered_then_left"
    | "fatal_failure"
    | "abandoned"
    | null;
  terminalStage: TraceStage | null;
  reasonCode: string | null;
  roomId: string | null;
  userId: string | null;
  deviceId: string | null;
}

export type TraceConnectionState =
  | "connecting"
  | "connected"
  | "disconnected"
  | "failed"
  | "superseded";

/** Изменяемая проекция одного подключения к конкретной LiveKit room. */
export interface TraceConnection {
  connectionId: string;
  sessionId: string;
  attemptId: string;
  projectionRevision: number;
  ordinal: number;
  trigger: "initial_connect" | "focus_switch" | "call_id_changed";
  startedAt: string;
  endedAt: string | null;
  state: TraceConnectionState;
  callId: string | null;
  reasonCode: string | null;
}

interface TraceEventBase {
  eventId: string;
  sessionId: string;
  attemptId: string | null;
  parentAttemptId: string | null;
  connectionId: string | null;
  callId: string | null;
  roomId: string | null;
  userId: string | null;
  deviceId: string | null;
  timestamp: string;
  sessionElapsedMs: number;
  sequence: number;
  stage: TraceStage;
  severity: TraceSeverity;
  outcome: TraceOutcome;
  reasonCode: string | null;
  message: string | null;
  relatedEventId: string | null;
  durationMs: number | null;
  traceVersion: typeof TRACE_VERSION;
}

/** Wire event сохраняет связь discriminator eventType с его payload. */
export type TraceEvent = {
  [EventType in TraceEventType]: TraceEventBase & {
    eventType: EventType;
    payload: TraceEventPayloadMap[EventType] | TruncatedTracePayload;
  };
}[TraceEventType];

/** Локальное состояние доставки, не входящее в append-only wire event. */
export interface StoredTraceEvent extends TraceEventBase {
  eventType: TraceEventType;
  payload: TraceWirePayload;
  deliveryState: TraceDeliveryState;
  approximateBytes: number;
}

export interface TraceUpload {
  uploadId: string;
  sessionId: string;
  firstSequence: number;
  lastSequence: number;
  eventCount: number;
  bytes: number;
  state: "pending" | "in_flight" | "retry_wait" | "acknowledged" | "dead";
  attemptCount: number;
  createdAt: string;
  lastAttemptAt: string | null;
  nextAttemptAt: string | null;
  httpStatus: number | null;
  errorCode: string | null;
  leaseOwner: string | null;
  leaseUntil: string | null;
}

export interface TraceEnvelope {
  traceVersion: typeof TRACE_VERSION;
  uploadId: string;
  session: TraceSession;
  attempts: TraceAttempt[];
  connections: TraceConnection[];
  events: TraceEvent[];
}

interface RecordTraceEventInputBase {
  stage: TraceStage;
  severity?: TraceSeverity;
  outcome?: TraceOutcome;
  reasonCode?: string | null;
  message?: string | null;
  relatedEventId?: string | null;
  durationMs?: number | null;
}

/** Discriminated union публичного API журнала. */
export type RecordTraceEventInput = {
  [EventType in TraceEventType]: RecordTraceEventInputBase & {
    eventType: EventType;
  } & (TraceEventPayloadMap[EventType] extends EmptyTracePayload
      ? { payload?: TraceEventPayloadMap[EventType] }
      : { payload: TraceEventPayloadMap[EventType] });
}[TraceEventType];

export interface TraceUploadAcknowledgement {
  traceVersion: typeof TRACE_VERSION;
  uploadId: string;
  acceptedEventCount: number;
}
